// ~40 LOC typed fetch wrapper — vendored locally rather than depending on
// `openapi-fetch` (in maintenance mode as of the openapi-ts 2026 roadmap).
// Auth: VITE_ADMIN_TOKEN set → X-Admin-Token (local dev); otherwise a Firebase
// ID token as `Authorization: Bearer`. Throws on non-2xx; a 403 (authenticated
// but no admin claim) flips the auth gate to the not-authorized screen, which
// is why no admin endpoint refuses anything else with a 403. A body makes the
// call a JSON POST (the «Betrieb» and Fragen-Check changes).

import { getIdToken } from "@/auth/firebase";
import { markForbidden } from "@/auth/store";

const ADMIN_TOKEN: string = import.meta.env.VITE_ADMIN_TOKEN || "";
// Local dev uses the relative path so Vite's proxy (vite.config.ts) forwards
// /admin/api to 127.0.0.1:8080 same-origin. The hosted SPA lives on a different
// origin (pulse.chat.gateway.one) than the backend, so a production build calls
// the Cloud Run service through the LB (chat.gateway.one) — which requires the
// admin origin in the backend's CORS allowlist. VITE_API_BASE_URL overrides both.
const BASE =
  import.meta.env.VITE_API_BASE_URL ||
  (import.meta.env.PROD ? "https://chat.gateway.one/admin/api" : "/admin/api");
// The erasure routes (session delete, lookup of a login's sessions) live under
// the sibling prefix on the same host and service.
const ERASURE_BASE = BASE.replace(/\/admin\/api$/, "/admindashboard/api");

export interface ApiError extends Error {
  status: number;
  body: unknown;
}

// Dev bypass wins when present; otherwise the Bearer ID token (auto-refreshed
// by the SDK on its ~1h expiry). Empty object when signed out — the call then
// 401s and the gate already shows Login.
async function authHeaders(): Promise<Record<string, string>> {
  if (ADMIN_TOKEN) return { "X-Admin-Token": ADMIN_TOKEN };
  const token = await getIdToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function call<T>(
  path: string,
  signal?: AbortSignal,
  body?: unknown,
  base: string = BASE,
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(await authHeaders()),
  };
  const init: RequestInit =
    body === undefined
      ? { headers, signal }
      : {
          method: "POST",
          headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify(body),
          signal,
        };
  const r = await fetch(`${base}${path}`, init);
  if (!r.ok) {
    if (r.status === 403) markForbidden();
    let body: unknown = null;
    try {
      body = await r.json();
    } catch {
      body = await r.text().catch(() => null);
    }
    const err = new Error(`HTTP ${r.status} on ${path}`) as ApiError;
    err.status = r.status;
    err.body = body;
    throw err;
  }
  return (await r.json()) as T;
}

import type {
  ActivityResponse,
  AggregatesResponse,
  AnalystRequest,
  AnalystResponse,
  BudgetsBody,
  CheckStepResponse,
  ConfigResponse,
  CostResponse,
  CustomerBody,
  DeletedResponse,
  DeleteSessionResponse,
  EngagementResponse,
  EstimateResponse,
  EventsTailResponse,
  HealthResponse,
  InsightListResponse,
  InsightReport,
  InsightRunRequest,
  KpiScorecardResponse,
  ModeSwitchBody,
  PartnerReportResponse,
  PeakResponse,
  PreviewToken,
  PromptsResponse,
  QuestionCheck,
  QuestionCheckCreate,
  QuestionCheckDetail,
  QuestionCheckEstimate,
  QuestionChecksResponse,
  QuestionList,
  QuestionListCreate,
  QuestionListsResponse,
  QuietHoursBody,
  RagAuditResponse,
  RecentQAResponse,
  RuntimeChange,
  RuntimeView,
  SessionDetailResponse,
  SessionListResponse,
  SignalsResponse,
  SnapshotResponse,
  TalentGeoResponse,
  TenantsResponse,
  UserSessionsBody,
  UserSessionsResponse,
  WindowLabel,
  WipeBody,
  WipePreview,
  WipeResponse,
} from "./types";

const qs = (params: Record<string, string | number | boolean | undefined>) => {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined) continue;
    search.set(k, String(v));
  }
  const s = search.toString();
  return s ? `?${s}` : "";
};

// Every windowed panel takes the same three filters. `tenant` is omitted from
// the query string when empty so an unfiltered call stays byte-identical to
// what it was before the partner dimension existed.
interface Scope {
  window: WindowLabel;
  includeTest: boolean;
  tenant?: string;
}

const scopeParams = ({ window: w, includeTest, tenant }: Scope) => ({
  window: w,
  include_test: includeTest,
  tenant: tenant || undefined,
});

// {discovery: "claude-opus-5-5"} → "discovery:claude-opus-5-5"; omitted when empty.
const modelChoices = (models: Record<string, string>) =>
  Object.entries(models)
    .map(([mode, model]) => `${mode}:${model}`)
    .join(",") || undefined;

export const api = {
  health: (signal?: AbortSignal) => call<HealthResponse>("/health", signal),

  config: (signal?: AbortSignal) => call<ConfigResponse>("/config", signal),

  // Live system prompts per mode — rendered by the SAME builders the chat
  // routes call, so the dashboard shows exactly what runs.
  prompts: (signal?: AbortSignal) => call<PromptsResponse>("/prompts", signal),

  // Persisted weekly aggregates (task #27 job output). No window param — each
  // doc is one completed ISO week.
  aggregates: (signal?: AbortSignal) => call<AggregatesResponse>("/aggregates", signal),

  // The partner dimension itself: who embeds us, plus the origins no tenant
  // claims yet. Drives the header's tenant selector.
  tenants: (scope: Omit<Scope, "tenant">, signal?: AbortSignal) =>
    call<TenantsResponse>(`/tenants${qs(scopeParams(scope))}`, signal),

  // One partner's report. `minN` is the k-anonymity floor on the CONTENT
  // breakdowns — keep it at 10 for anything shared with the partner.
  partnerReport: (
    { minN, ...scope }: Scope & { tenant: string; minN: number },
    signal?: AbortSignal,
  ) =>
    call<PartnerReportResponse>(
      `/partner-report${qs({ ...scopeParams(scope), min_n: minN })}`,
      signal,
    ),

  snapshot: (scope: Scope, signal?: AbortSignal) =>
    call<SnapshotResponse>(`/snapshot${qs(scopeParams(scope))}`, signal),

  peak: (scope: Scope, signal?: AbortSignal) =>
    call<PeakResponse>(`/peak${qs(scopeParams(scope))}`, signal),

  // People who chatted per day, and the last 7 and 30 days against the ones
  // before. No window: its look-back is fixed.
  activity: ({ includeTest, tenant }: Omit<Scope, "window">, signal?: AbortSignal) =>
    call<ActivityResponse>(
      `/activity${qs({ include_test: includeTest, tenant: tenant || undefined })}`,
      signal,
    ),

  cost: (scope: Scope, signal?: AbortSignal) =>
    call<CostResponse>(`/cost${qs(scopeParams(scope))}`, signal),

  // Cost per person over the last 30 days and the scenario calculator. No
  // window: the scenarios multiply cost per person by people per month.
  // `caps` bounds the costs by the limits and the daily budget in force.
  estimate: (
    {
      people,
      models,
      caps,
      includeTest,
      tenant,
    }: Omit<Scope, "window"> & {
      people: number;
      models: Record<string, string>;
      caps?: boolean;
    },
    signal?: AbortSignal,
  ) =>
    call<EstimateResponse>(
      `/estimate${qs({
        include_test: includeTest,
        tenant: tenant || undefined,
        people,
        models: modelChoices(models),
        caps: caps || undefined,
      })}`,
      signal,
    ),

  engagement: (scope: Scope, signal?: AbortSignal) =>
    call<EngagementResponse>(`/engagement${qs(scopeParams(scope))}`, signal),

  kpiScorecard: (scope: Scope, signal?: AbortSignal) =>
    call<KpiScorecardResponse>(`/kpi-scorecard${qs(scopeParams(scope))}`, signal),

  ragAudit: (scope: Scope, signal?: AbortSignal) =>
    call<RagAuditResponse>(`/rag-audit${qs(scopeParams(scope))}`, signal),

  signals: (scope: Scope, signal?: AbortSignal) =>
    call<SignalsResponse>(`/signals${qs(scopeParams(scope))}`, signal),

  talentGeo: ({ minN, ...scope }: Scope & { minN: number }, signal?: AbortSignal) =>
    call<TalentGeoResponse>(
      `/talent-geo${qs({ ...scopeParams(scope), min_n: minN })}`,
      signal,
    ),

  recentSessions: (
    { limit, includeTest }: { limit: number; includeTest: boolean },
    signal?: AbortSignal,
  ) =>
    call<SessionListResponse>(
      `/sessions/recent${qs({ limit, include_test: includeTest })}`,
      signal,
    ),

  sessionDetail: (sessionId: string, signal?: AbortSignal) =>
    call<SessionDetailResponse>(`/sessions/${encodeURIComponent(sessionId)}`, signal),

  recentEvents: (
    { limit, eventType }: { limit?: number; eventType?: string } = {},
    signal?: AbortSignal,
  ) =>
    call<EventsTailResponse>(
      `/events/recent${qs({ limit, event_type: eventType })}`,
      signal,
    ),

  recentTurns: (
    { limit, includeTest }: { limit: number; includeTest: boolean },
    signal?: AbortSignal,
  ) =>
    call<RecentQAResponse>(
      `/turns/recent${qs({ limit, include_test: includeTest })}`,
      signal,
    ),

  // «Betrieb»: the runtime switches. Every change answers with the whole view,
  // so the panel shows what was saved, not what was asked for.
  runtime: (signal?: AbortSignal) => call<RuntimeView>("/runtime", signal),

  switchChat: (body: RuntimeChange & { enabled: boolean }) =>
    call<RuntimeView>("/runtime/chat", undefined, body),

  switchMode: (mode: string, body: ModeSwitchBody) =>
    call<RuntimeView>(`/runtime/modes/${encodeURIComponent(mode)}`, undefined, body),

  setBudgets: (body: BudgetsBody) => call<RuntimeView>("/runtime/budgets", undefined, body),

  setQuietHours: (body: QuietHoursBody) =>
    call<RuntimeView>("/runtime/quiet-hours", undefined, body),

  // A customer and its pages; no origins removes it.
  setCustomer: (tenant: string, body: CustomerBody) =>
    call<RuntimeView>(`/runtime/customers/${encodeURIComponent(tenant)}`, undefined, body),

  // Minted per call (a POST): the test page's embed makes its sessions test sessions.
  previewToken: (signal?: AbortSignal) =>
    call<PreviewToken>("/runtime/preview-token", signal, {}),

  // One answer of the analysis chat over the window's data pack, or over one
  // Fragen-Check when the body names it.
  analyst: (body: AnalystRequest) => call<AnalystResponse>("/analyst", undefined, body),

  // Tagesbericht: the stored days (newest first), one day's report, and a run
  // by hand that replaces the day's report. A run is one model call, up to
  // about three minutes: no client timeout.
  insights: (signal?: AbortSignal) => call<InsightListResponse>("/insights", signal),

  insight: (day: string, signal?: AbortSignal) =>
    call<InsightReport>(`/insights/${encodeURIComponent(day)}`, signal),

  runInsight: (body: InsightRunRequest) =>
    call<InsightReport>("/insights/run", undefined, body),

  // Fragen-Check: pasted search queries grouped into a stored list, and checks
  // that run a selection of them through the FAQ as test sessions, one step
  // per call. Deleting is a POST, like every change here.
  questionLists: (signal?: AbortSignal) =>
    call<QuestionListsResponse>("/question-check/lists", signal),

  questionList: (id: string, signal?: AbortSignal) =>
    call<QuestionList>(`/question-check/lists/${encodeURIComponent(id)}`, signal),

  // What the rules leave, a model places in batches: mostly under a minute,
  // up to about three with very many unplaced rows. No client timeout.
  createQuestionList: (body: QuestionListCreate) =>
    call<QuestionList>("/question-check/lists", undefined, body),

  deleteQuestionList: (id: string) =>
    call<DeletedResponse>(`/question-check/lists/${encodeURIComponent(id)}/delete`, undefined, {}),

  // Reads the last 30 days of FAQ turns. Its per-question basis is the same for
  // every `n`, so the view asks once per list (`estimateTotals` multiplies).
  questionCheckEstimate: (n: number, signal?: AbortSignal) =>
    call<QuestionCheckEstimate>(`/question-check/estimate${qs({ n })}`, signal),

  questionChecks: (signal?: AbortSignal) =>
    call<QuestionChecksResponse>("/question-check/checks", signal),

  questionCheck: (id: string, signal?: AbortSignal) =>
    call<QuestionCheckDetail>(`/question-check/checks/${encodeURIComponent(id)}`, signal),

  createQuestionCheck: (body: QuestionCheckCreate) =>
    call<QuestionCheck>("/question-check/checks", undefined, body),

  // Runs the next few questions; the results are the ones this step finished.
  questionCheckNext: (id: string) =>
    call<CheckStepResponse>(`/question-check/checks/${encodeURIComponent(id)}/next`, undefined, {}),

  deleteQuestionCheck: (id: string) =>
    call<DeletedResponse>(`/question-check/checks/${encodeURIComponent(id)}/delete`, undefined, {}),

  wipePreview: (signal?: AbortSignal) => call<WipePreview>("/runtime/wipe", signal),

  wipe: (body: WipeBody) => call<WipeResponse>("/runtime/wipe", undefined, body),

  // Right to erasure: the sessions of a login (by name or user hash), and the
  // delete of ONE session with its turns and events. No bulk delete here.
  userSessions: (body: UserSessionsBody) =>
    call<UserSessionsResponse>("/user/sessions", undefined, body, ERASURE_BASE),

  deleteSession: (sessionId: string, reason: string) =>
    call<DeleteSessionResponse>(
      `/delete/session/${encodeURIComponent(sessionId)}`,
      undefined,
      { reason },
      ERASURE_BASE,
    ),
};

export { ADMIN_TOKEN };
